import { useCustom } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Barcode,
  MapPin,
  QrCode,
  Ruler,
  Search,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { CodeSheet, type CodeRequest } from "@/components/CodeSheet";
import { MoveFeed } from "@/components/MoveFeed";
import { RecardSheet } from "@/components/RecardSheet";
import { SpisanieSheet } from "@/components/SpisanieSheet";
import { ApiError, query } from "@/lib/api";
import { DATA_QUERIES } from "@/lib/providers";
import { errorMessage, moveWord, stamp } from "@/lib/format";
import type { MoveHistory, MovePoint, Serial } from "@/lib/providers";
import { useSessionGuard } from "@/lib/useSessionGuard";

const FIELD =
  "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[13px] focus:border-border-strong";

/**
 * Сколько строк ленты показывать сразу и сколько добавляет одно «ещё».
 *
 * Десять — это разбор «где вещь была последнее время», с которым карточку и
 * открывают; полная история вещи бывает в сотни переездов и заслонила бы
 * собой всё остальное на экране.
 */
const PAGE = 10;

/** Потолок ленты на стороне сервиса (`crm/move.py::HISTORY_MAX`). */
const HISTORY_MAX = 200;

/**
 * Карточка СЕРИИ — одной физической вещи.
 *
 * Раздел «Товары» отвечает на вопрос «сколько их у нас и где», эта карточка —
 * на вопрос «что с ЭТОЙ вещью»: её открывают, когда номер уже в руках — с
 * ярлыка, из ленты списаний, из чужого разбора.
 *
 * Четыре ответа, и они идут в порядке вопросов склада:
 *
 *     где лежит      место, город, код места — по нему вещь ищут руками
 *     как попала     переезд из `sSerial.moveID`: откуда, когда, кто
 *     что с ней      списана или в обороте, и если списана — по какой причине
 *     что это        товар маркетплейса: баркод, размер, цвет
 *
 * Лента движений внизу — ОТДЕЛЬНАЯ дверь (`GET /api/move/history`), та же,
 * что у раннера серии. Карточка её не копирует: два ответа на «где вещь
 * была» разошлись бы порядком строк ровно тогда, когда их начали бы сверять.
 *
 * ДЕЙСТВИЙ У ЭКРАНА ДВА, и каждое стоит в разделе, который оно меняет:
 * «Списать» — в разделе «Списание», «Сменить размер» — в разделе «Что это».
 * Второе заведено 22.09.26: вещь лежит не тем размером, каким числится, и до
 * этой кнопки исправить такое можно было только в CRM руками.
 *
 * Первое из них — кнопка «Списать» (задача владельца 16.09.26),
 * и стоит она в разделе «Списание»: он и есть то, что действие меняет. Своей
 * двери у неё нет — зовёт она `POST /api/spisanie`, ту самую, что списывает
 * вещь ПО НОМЕРУ СЕРИИ и существует с 06.09.26. Второе определение списания
 * здесь разошлось бы с журналом на первой правке.
 */
export function SerialPage() {
  const { serial = "" } = useParams();
  const navigate = useNavigate();
  const queries = useQueryClient();
  const [code, setCode] = useState<CodeRequest | null>(null);
  // Открыто ли окно списания. `false` — закрыто, и это ЕДИНСТВЕННОЕ состояние,
  // из которого списание не может случиться промахом пальца.
  const [writeOff, setWriteOff] = useState(false);
  // То же для окна пересортицы: своё состояние, а не общее с окном списания.
  // Два необратимых действия, делящих один флаг, однажды открылись бы одно
  // вместо другого.
  const [recard, setRecard] = useState(false);
  // Поле поиска живёт в состоянии, а сам запрос — в АДРЕСЕ: карточку серии
  // пересылают сменщику ссылкой, и она обязана открываться у него такой же.
  const [draft, setDraft] = useState(serial);
  useEffect(() => setDraft(serial), [serial]);
  // Новая вещь — закрытые окна: они были про ПРЕЖНЮЮ серию и остаться
  // открытыми над другой вещью не имеют права.
  useEffect(() => {
    setWriteOff(false);
    setRecard(false);
  }, [serial]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = draft.trim();
    // Только цифры: номер серии — число, и «abc» ушло бы в 422 от сервиса
    // вместо честного «наберите номер».
    if (!/^\d+$/.test(next)) return;
    navigate(`/serial/${next}`);
  };

  const serialQuery = useCustom<Serial>({
    url: `/api/serial/${encodeURIComponent(serial)}`,
    method: "get",
    queryOptions: { enabled: /^\d+$/.test(serial) },
  });

  const loaded = serialQuery.result.data;
  // Серией считается только payload с номером: `result.data` у Refine
  // существует и до загрузки — пустым объектом.
  const item = loaded && typeof loaded.serial === "number" ? loaded : null;
  const error = serialQuery.query.error;
  useSessionGuard(error);

  return (
    <main
      className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:p-5"
      data-component="SerialPage"
      data-annot="серия"
    >
      <form
        onSubmit={submit}
        data-annot="поиск серии"
        className="flex flex-wrap items-center gap-2"
      >
        <label className="text-[12.5px] text-fg-muted" htmlFor="serial-number">
          Серия
        </label>
        <input
          id="serial-number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          inputMode="numeric"
          placeholder="номер с ярлыка"
          className={FIELD + " tnum w-[160px]"}
        />
        <button
          type="submit"
          className="flex h-8 items-center gap-1.5 rounded-[var(--radius-ui)] bg-fg px-3 text-[13px] font-medium text-bg"
        >
          <Search size={13} />
          Открыть
        </button>
      </form>

      {!serial ? (
        <Empty />
      ) : error ? (
        <Failed serial={serial} error={error} />
      ) : !item ? (
        <p className="text-[13px] text-fg-muted">Загружаю…</p>
      ) : (
        <>
          <Head item={item} />
          <div className="grid gap-4 md:grid-cols-2">
            <Where item={item} onCode={setCode} />
            <What item={item} onCode={setCode} onRecard={() => setRecard(true)} />
          </div>
          <Spisanie item={item} onWriteOff={() => setWriteOff(true)} />
          <History serial={item.serial} />
        </>
      )}

      {code ? (
        <CodeSheet request={code} onClose={() => setCode(null)} />
      ) : null}

      {/* ОКНО СПИСАНИЯ. Карточка после него ДРУГАЯ целиком — состояние вещи,
          строка списания и лента, — поэтому перечитываются оба запроса экрана:
          показывать «Доступна» рядом со словами «списано» значило бы спорить с
          самим собой. Перечитываются ИМЕННО ДАННЫЕ (`DATA_QUERIES`), см. там. */}
      {writeOff && item ? (
        <SpisanieSheet
          aim={{ kind: "serial", item }}
          onClose={() => setWriteOff(false)}
          onDone={() => queries.invalidateQueries({ queryKey: DATA_QUERIES })}
        />
      ) : null}

      {/* ОКНО ПЕРЕСОРТИЦЫ. Перечитывается то же, что после списания, и по той
          же причине: у вещи меняется карточка, а с ней баркод, размер и цвет —
          то есть половина того, что на экране написано. */}
      {recard && item ? (
        <RecardSheet
          item={item}
          onClose={() => setRecard(false)}
          onDone={() => queries.invalidateQueries({ queryKey: DATA_QUERIES })}
        />
      ) : null}
    </main>
  );
}

/** Номер не набран: экран открыли из меню, а не по ссылке. */
function Empty() {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
      Наберите номер серии — или отсканируйте ярлык вещи.
    </div>
  );
}

/**
 * Отказ. 404 отделён от прочего намеренно: «такой серии нет» чинится
 * исправленным номером, а недоступный сервис — ожиданием, и общее слово
 * «ошибка» отправило бы искать не то.
 */
function Failed({ serial, error }: { serial: string; error: unknown }) {
  const missing = error instanceof ApiError && error.status === 404;
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-3 rounded-[var(--radius-card)] border border-err/60 bg-err-bg p-6 text-center"
    >
      <AlertTriangle size={20} className="text-err" />
      <p className="text-[13.5px]">
        {missing ? `Серии ${serial} нет в CRM` : errorMessage(error)}
      </p>
    </div>
  );
}

/** Номер, состояние и то, чем вещь является одной строкой. */
function Head({ item }: { item: Serial }) {
  return (
    <header className="flex flex-wrap items-start gap-4" data-annot="шапка серии">
      {item.card?.image_url ? (
        <img
          src={item.card.image_url}
          alt=""
          referrerPolicy="no-referrer"
          className="h-[132px] w-[96px] flex-none rounded-[var(--radius-ui)] border border-border object-cover"
          onError={(e) => {
            e.currentTarget.style.visibility = "hidden";
          }}
        />
      ) : null}
      <div className="min-w-[240px] flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="tnum text-[22px] font-semibold tracking-[-0.02em]">
            Серия {item.serial}
          </h1>
          <State item={item} />
        </div>
        <p className="mt-1 text-[13.5px] text-fg-muted">
          {item.card?.name || "Товар на маркетплейсе не заведён"}
        </p>
        <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px]">
          {/* Артикул серии — не артикул товара: у вещи он свой и совпадает с
              номером серии у всего, что заведено поштучно. Оба показываются
              там, где о них спрашивают: этот здесь, товарный — в разделе
              «Что это». */}
          <Pair label="Артикул" value={String(item.article)} mono />
          {item.size ? <Pair label="Размер вещи" value={item.size} /> : null}
          {item.color ? <Pair label="Цвет" value={item.color} /> : null}
          {item.date_arrive ? (
            <Pair label="Поступила" value={stamp(item.date_arrive)} mono />
          ) : null}
        </dl>
      </div>
    </header>
  );
}

/**
 * Состояние вещи чипом. Списанная вещь физически на складе есть, но в оборот
 * не идёт — на экране это обязано читаться раньше всего остального.
 */
function State({ item }: { item: Serial }) {
  const live = item.available === 1;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span
        data-annot="состояние"
        className={
          "rounded border px-1.5 py-0.5 text-[11.5px] font-medium " +
          (live
            ? "border-ok/60 bg-ok-bg text-ok"
            : "border-err/60 bg-err-bg text-err")
        }
      >
        {live ? "Доступна" : "Списана"}
      </span>
      {/* Удалённая серия — третье состояние, и молчать о нём нельзя: такой
          вещи в учёте больше нет, а карточка её всё ещё показывает. */}
      {item.deleted ? (
        <span className="rounded border border-err/60 bg-err-bg px-1.5 py-0.5 text-[11.5px] font-medium text-err">
          Удалена
        </span>
      ) : null}
    </span>
  );
}

/** Где вещь лежит и как она сюда попала. */
function Where({
  item,
  onCode,
}: {
  item: Serial;
  onCode: (code: CodeRequest) => void;
}) {
  const place = item.place;
  return (
    <Panel title="Где лежит" annot="место">
      {place === null ? (
        <p className="text-[13px] text-fg-muted">Места у серии нет.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <MapPin size={14} className="flex-none text-fg-muted" />
            <span className="text-[15px] font-medium">
              {place.name || `Место ${place.place_id}`}
            </span>
            {place.city ? (
              <span className="rounded border border-border bg-bg-elevated px-1.5 py-px text-[11.5px] text-fg-muted">
                {place.city}
              </span>
            ) : null}
          </div>
          <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px]">
            <Pair label="Номер места" value={String(place.place_id)} mono />
            {place.group ? <Pair label="Группа" value={place.group} /> : null}
            {place.status ? <Pair label="Статус места" value={place.status} /> : null}
            {item.place_date ? (
              <Pair label="Здесь с" value={stamp(item.place_date)} mono />
            ) : null}
          </dl>
          {/* Код места рисуется QR по той же причине, что баркод товара —
              Code 128: его СКАНИРУЮТ. Набирать двенадцать цифр руками,
              держа сканер в другой руке, — это то, ради чего экран и
              открывают. */}
          {place.barcode ? (
            <button
              type="button"
              onClick={() =>
                onCode({
                  kind: "qr",
                  value: place.barcode as string,
                  title: `Код места · ${place.name || place.place_id}`,
                })
              }
              title="Показать QR с кодом места"
              data-annot="код места"
              className="mt-2 flex items-center gap-1.5 rounded-[var(--radius-ui)] border border-border px-2 py-1 text-[12.5px] hover:bg-bg-hover"
            >
              <span className="tnum">{place.barcode}</span>
              <QrCode size={14} className="flex-none text-fg-muted" />
            </button>
          ) : null}
          <Arrived item={item} />
        </>
      )}
    </Panel>
  );
}

/**
 * Как вещь попала на текущее место — тот самый переезд, на который указывает
 * сама серия (`sSerial.moveID`).
 *
 * Стоит рядом с местом, а не в ленте внизу: «откуда приехала» — половина
 * ответа на «где лежит», и ради неё не должно приходиться листать историю.
 */
function Arrived({ item }: { item: Serial }) {
  const came = item.arrived;
  if (came === null) {
    return (
      <p className="mt-3 border-t border-border pt-2 text-[12.5px] text-fg-muted">
        Перемещений у серии нет — она лежит там, где её завели.
      </p>
    );
  }
  return (
    <div className="mt-3 border-t border-border pt-2" data-annot="приехала">
      <p className="text-[11px] uppercase tracking-[0.04em] text-fg-muted">
        Приехала
      </p>
      <p className="mt-1 text-[13px]">
        из <PlaceName point={came.from} />
      </p>
      <p className="mt-0.5 text-[12.5px] text-fg-muted">
        <span className="tnum">{stamp(came.at)}</span>
        {came.type ? ` · ${came.type}` : ""}
        {came.user ? ` · ${came.user}` : ""}
      </p>
    </div>
  );
}

/** Что это за вещь: товар маркетплейса с баркодом и размером. */
function What({
  item,
  onCode,
  onRecard,
}: {
  item: Serial;
  onCode: (code: CodeRequest) => void;
  onRecard: () => void;
}) {
  const card = item.card;
  return (
    <Panel title="Что это" annot="товар">
      {card === null ? (
        <p className="text-[13px] text-fg-muted">
          Серия не привязана к карточке маркетплейса — баркода и размера у неё
          нет.
        </p>
      ) : (
        <>
          <p className="text-[15px] font-medium">{card.name || "—"}</p>
          {/* Баркод КНОПКОЙ, а не числом: он существует, чтобы его
              сканировали, и Code 128 здесь — тот же, что на карточке
              товара. */}
          <button
            type="button"
            onClick={() =>
              onCode({
                kind: "barcode",
                value: card.barcode,
                title: `Баркод · размер ${card.size || "—"}`,
              })
            }
            title="Показать штрихкод Code 128"
            data-annot="баркод"
            className="mt-2 flex items-center gap-1.5 rounded-[var(--radius-ui)] border border-border px-2 py-1 text-[13px] hover:bg-bg-hover"
          >
            <span className="tnum font-medium">{card.barcode}</span>
            <Barcode size={14} className="flex-none text-fg-muted" />
          </button>
          <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px]">
            <Pair label="Размер" value={card.size || "—"} />
            <Pair label="Цвет" value={card.color || "—"} />
            <Pair label="Артикул товара" value={card.article || "—"} mono />
            <Pair label="Карточка" value={String(card.card_id)} mono />
            {/* Ноль означает «на Ozon не заведён» — так и сказано словами:
                «0» в колонке идентификатора читается как настоящий SKU. */}
            <Pair
              label="Ozon SKU"
              value={card.ozon_sku ? String(card.ozon_sku) : "не заведён"}
              mono={!!card.ozon_sku}
            />
          </dl>
          {/* Соседний вопрос, который задают с этой же карточки: «а где ЕЩЁ
              такие лежат и откуда мы их брали». Он про размер, а не про вещь,
              и отвечает на него раздел «Баркод». */}
          <Link
            to={`/barcode/${card.barcode}`}
            data-annot="к баркоду"
            className="mt-2 flex w-fit items-center gap-1.5 text-[12.5px] text-fg-muted hover:text-fg"
          >
            <ArrowLeft size={13} className="rotate-180" />
            Где лежат и где лежали вещи этого баркода
          </Link>
          {card.model_id ? (
            <Link
              to={`/products/${card.model_id}`}
              data-annot="к товару"
              className="mt-2 flex w-fit items-center gap-1.5 text-[12.5px] text-fg-muted hover:text-fg"
            >
              <ArrowLeft size={13} className="rotate-180" />
              Все размеры и остатки товара {card.model_id}
            </Link>
          ) : null}
          <Recard item={item} onRecard={onRecard} />
        </>
      )}
    </Panel>
  );
}

/**
 * КНОПКА «СМЕНИТЬ РАЗМЕР» — вторая правка, доступная с карточки серии.
 *
 * Стоит в разделе «Что это», а не в шапке: раздел и есть то, что она меняет —
 * после неё здесь будут другой баркод, другой размер и другой Ozon SKU. То же
 * правило, по которому «Списать» живёт в разделе «Списание».
 *
 * ЕСТЬ ТОЛЬКО У ВЕЩИ С КАРТОЧКОЙ И С МОДЕЛЬЮ. Размеры для выбора приезжают
 * размерами МОДЕЛИ, и у вещи, на маркетплейс не заведённой, выбирать не из
 * чего: окно открылось бы пустым списком. Первичную привязку делает приёмка,
 * а не пересортица.
 *
 * У СПИСАННОЙ ВЕЩИ КНОПКА ОСТАЁТСЯ, и это не недосмотр. Дверь списанную вещь
 * принимает намеренно (как и оригинал CRM): пересорт обнаруживают и после
 * того, как вещь ушла с учёта, а неверный размер в истории остаётся неверным.
 * Нет её только у УДАЛЁННОЙ серии — такой вещи в CRM больше нет вовсе.
 */
function Recard({ item, onRecard }: { item: Serial; onRecard: () => void }) {
  if (!item.card?.model_id || item.deleted) return null;

  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-border pt-2">
      <button
        type="button"
        onClick={onRecard}
        title="Перевести вещь на другой размер этой модели"
        data-annot="сменить размер"
        className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover"
      >
        <Ruler size={14} className="flex-none text-fg-muted" />
        Сменить размер
      </button>
      {/* Сказано ДО нажатия, как и у «Списать»: человек решает, жать ли, здесь. */}
      <span className="text-[12.5px] text-fg-muted">
        Если вещь лежит не тем размером, каким числится.
      </span>
    </div>
  );
}

/**
 * Списание: было или нет, по какой причине и чьей рукой.
 *
 * Состояние вещи и строка списания — РАЗНЫЕ вещи, и раздел показывает обе.
 * `available` серии говорит, списана ли она сейчас; строка (`spisanieID`)
 * говорит, чем закончилась последняя операция: списанием или возвратом в
 * оборот. У вещи, которую списали и вернули, состояние «Доступна», а строка
 * — про возврат, и без обеих половин это не прочитать.
 */
function Spisanie({
  item,
  onWriteOff,
}: {
  item: Serial;
  onWriteOff: () => void;
}) {
  const row = item.spisanie;
  const live = item.available === 1;

  if (row === null) {
    return (
      <Panel title="Списание" annot="списание">
        <p className="text-[13px] text-fg-muted">
          {live
            ? "Серия в обороте, списаний у неё не было."
            : "Серия списана, но строки списания в CRM нет — состояние поменяли мимо неё."}
        </p>
        <WriteOff item={item} onWriteOff={onWriteOff} />
      </Panel>
    );
  }

  return (
    <Panel title="Списание" annot="списание">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[15px] font-medium">
          {row.category || `Категория ${row.category_id}`}
        </span>
        <span className="rounded border border-border bg-bg-elevated px-1.5 py-px text-[11.5px] text-fg-muted">
          № {row.category_id}
        </span>
        {/* Что сделала ЭТА строка. У серии в обороте она читается как
            «списание сняли», и без этой пометки категория «fbsozon» рядом со
            статусом «Доступна» выглядела бы противоречием. */}
        <span className="rounded border border-border bg-bg-elevated px-1.5 py-px text-[11.5px] text-fg-muted">
          {row.available === 1 ? "возврат в оборот" : "списание"}
        </span>
      </div>
      <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px]">
        <Pair label="Когда" value={stamp(row.at)} mono />
        {row.user ? <Pair label="Кто" value={row.user} /> : null}
        <Pair label="Номер" value={String(row.spisanie_id)} mono />
      </dl>
      {row.comment ? (
        <p className="mt-2 rounded-[var(--radius-ui)] border border-border bg-bg p-2 text-[12.5px] text-fg-muted">
          {row.comment}
        </p>
      ) : null}
      <WriteOff item={item} onWriteOff={onWriteOff} />
    </Panel>
  );
}

/**
 * КНОПКА «СПИСАТЬ» — единственное действие карточки (задача владельца
 * 16.09.26).
 *
 * Стоит в разделе «Списание», а не в шапке рядом с номером: раздел и есть то,
 * что кнопка меняет, — после операции здесь появится строка, а состояние вещи
 * станет «Списана». Действие рядом со своим следствием читается без объяснений.
 *
 * ПОКАЗЫВАЕТСЯ ТОЛЬКО У ВЕЩИ В ОБОРОТЕ. У списанной списывать нечего: дверь
 * ответила бы `already`, не написав ни строки, — то есть кнопка обещала бы
 * работу, которой не будет. Вместо неё сказано словами, почему её нет.
 *
 * У УДАЛЁННОЙ ВЕЩИ ЕЁ НЕТ ТОЖЕ. Удалённая серия — не состояние учёта, а его
 * отсутствие: такой вещи в CRM больше нет, и списывать с учёта то, чего в учёте
 * не значится, значит заводить строку о несуществующем.
 */
function WriteOff({
  item,
  onWriteOff,
}: {
  item: Serial;
  onWriteOff: () => void;
}) {
  const live = item.available === 1;
  const gone = !!item.deleted;

  if (!live || gone) {
    return (
      <p className="mt-3 border-t border-border pt-2 text-[12.5px] text-fg-muted">
        {gone
          ? "Серия удалена из CRM — списывать нечего."
          : "Вещь уже списана. Вернуть её в оборот можно только в CRM."}
      </p>
    );
  }

  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-border pt-2">
      <button
        type="button"
        onClick={onWriteOff}
        title="Снять эту вещь с учёта"
        data-annot="списать"
        className="inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:border-err/60 hover:bg-err-bg"
      >
        <Trash2 size={14} className="flex-none text-fg-muted" />
        Списать
      </button>
      {/* Сказано ДО нажатия, а не только в окне: человек решает, жать ли, здесь. */}
      <span className="text-[12.5px] text-fg-muted">
        Вещь уйдёт с учёта под вашей подписью. Операция необратима.
      </span>
    </div>
  );
}

/**
 * Лента движений: где вещь была и куда уезжала.
 *
 * «Ещё» поднимает ПОТОЛОК одного запроса, а не склеивает страницы. Склейка на
 * живом складе показала бы переезд дважды или потеряла его: между двумя
 * запросами вещь могут сдвинуть, и вторая страница поедет по сместившейся
 * границе. Тот же приём, что у «показать ещё» в списке товаров.
 */
function History({ serial }: { serial: number }) {
  const [limit, setLimit] = useState(PAGE);
  // Новая серия — новая лента: потолок, поднятый на прошлой вещи, к этой
  // отношения не имеет.
  useEffect(() => setLimit(PAGE), [serial]);

  const historyQuery = useCustom<MoveHistory>({
    url: `/api/move/history${query({ serial, limit })}`,
    method: "get",
  });
  const loaded = historyQuery.result.data;
  const moves = loaded && Array.isArray(loaded.moves) ? loaded.moves : [];
  const busy = historyQuery.query.isFetching;
  const error = historyQuery.query.error;

  return (
    <section data-annot="история">
      <h2 className="mb-2 flex flex-wrap items-baseline gap-x-2 text-[15px] font-semibold">
        История перемещений{" "}
        {/* «6 переездов» и «20 переездов · есть ещё»: число без слова
            читается как номер, а «20 и ещё» — как оборванная фраза. */}
        <span className="font-normal text-fg-muted">
          {moves.length} {moveWord(moves.length)}
          {loaded?.has_more ? " · есть ещё" : ""}
        </span>
      </h2>

      {error ? (
        <div
          role="alert"
          className="rounded-[var(--radius-card)] border border-err/60 bg-err-bg p-4 text-center text-[13px]"
        >
          {errorMessage(error)}
        </div>
      ) : moves.length === 0 ? (
        <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
          {busy ? "Загружаю…" : "Эту вещь ещё ни разу не двигали."}
        </div>
      ) : (
        // Таблица ОБЩАЯ на все ленты склада (`components/MoveFeed.tsx`).
        // Колонки «Серия» здесь нет намеренно: строки одной вещи, и она была
        // бы столбцом одного повторяющегося числа.
        <MoveFeed
          moves={moves}
          tasksKnown={loaded?.tasks_known ?? false}
          caption="Перемещения серии: когда, откуда, куда, каким типом и чьей рукой"
        />
      )}

      {/* Кнопка живёт по признаку «есть ещё» от сервиса, а не по числу строк:
          он считает лишнюю строку сам и знает про хвост то, чего не знает
          экран. Потолок ленты — 200 строк, дальше просить нечего. */}
      {loaded?.has_more && limit < HISTORY_MAX ? (
        <button
          type="button"
          onClick={() => setLimit((now) => Math.min(now + PAGE, HISTORY_MAX))}
          disabled={busy}
          data-annot="ещё"
          className="mt-2 h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover disabled:opacity-55"
        >
          {busy ? "Загружаю…" : `Ещё ${PAGE}`}
        </button>
      ) : null}
    </section>
  );
}

/**
 * Конец переезда: имя места и город под ним.
 *
 * Город печатается ОТДЕЛЬНО, а не подразумевается именем: имена мест несут
 * склад («Невиномыск Короб 10945»), номера коробов у городов сквозные, и
 * «Короб 4977» из Воронежа читался бы как ростовский.
 */
function PlaceName({ point }: { point: MovePoint }) {
  if (point.place_id === null) {
    return <span className="text-fg-muted">—</span>;
  }
  return (
    <span className="flex flex-col">
      <span>{point.name || `Место ${point.place_id}`}</span>
      {point.city || point.group ? (
        <span className="text-[11px] text-fg-muted">
          {[point.city, point.group].filter(Boolean).join(" · ")}
        </span>
      ) : null}
    </span>
  );
}

/** Раздел карточки: заголовок и содержимое в одной рамке. */
function Panel({
  title,
  annot,
  children,
}: {
  title: string;
  annot: string;
  children: React.ReactNode;
}) {
  return (
    <section
      data-annot={annot}
      className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3"
    >
      <h2 className="mb-2 text-[11px] uppercase tracking-[0.04em] text-fg-muted">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Pair({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-fg-muted">{label}</dt>
      <dd className={mono ? "tnum" : ""}>{value}</dd>
    </div>
  );
}

// Ячейка таблицы и заголовок колонки переехали в `components/MoveFeed.tsx`
// вместе с самой лентой (10.09.26): своих таблиц у карточки серии больше нет.
